import { useAuthContext } from '@repo/hooks';
import { apiRequest } from '@repo/utils/helpers/api';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AllowAccess, FinishingAuth, LoginError, LoginToClay, NoCredentialsError } from './views';

interface ChatGPTOauthParams {
  responseType: string;
  clientId: string;
  redirectUrl: string;
  scope?: string;
  state?: string;
}

export interface OauthParams {
  code: string;
  state?: string;
}

export type OauthLoginParams = Partial<ChatGPTOauthParams & OauthParams>;

const oauthLocalStorageKey = 'oauth-sso';

const storeOauthLoginParams = (params: OauthLoginParams) => {
  if (params) {
    localStorage.setItem(oauthLocalStorageKey, JSON.stringify(params));
  }
};

export const getStoredOauthParams = (): OauthLoginParams => {
  let params = localStorage.getItem(oauthLocalStorageKey);
  localStorage.removeItem(oauthLocalStorageKey);

  if (!params) {
    return undefined;
  }

  return JSON.parse(params);
};

export function OauthLogin({
  getOauthParams,
  postOauthParams,
  integration,
}: {
  getOauthParams: (searchParams: URLSearchParams) => Partial<OauthLoginParams> | undefined;
  postOauthParams: (oauthParams: OauthLoginParams) => Promise<any>;
  integration: 'chatgpt' | 'notion';
}) {
  const { user } = useAuthContext();
  const [searchParams] = useSearchParams();
  const [oauthParams, setOauthParams] = useState<OauthLoginParams | undefined>(undefined);
  const [missingLoginCreds, setMissingLoginCreds] = useState(false);
  const [didPostLoginCreds, setDidPostLoginCreds] = useState(false);
  const [didError, setDidError] = useState(false);
  const [userNeedsToLogin, setUserNeedsToLogin] = useState(false);

  const finishOauthLogin = async () => {
    setDidPostLoginCreds(true);

    try {
      const oauthLogin = await postOauthParams(oauthParams);
      window.location.href = oauthLogin.redirect_url;
    } catch (e) {
      console.error(e);
      setDidError(true);
    }
  };

  useEffect(() => {
    const oauthLoginParams = getOauthParams(searchParams);

    if (!oauthLoginParams) {
      setMissingLoginCreds(true);
    }

    setOauthParams(oauthLoginParams as OauthLoginParams);

    if (!user) {
      storeOauthLoginParams(oauthLoginParams as OauthLoginParams);
      setUserNeedsToLogin(true);
    }
  }, []);

  if (missingLoginCreds) {
    return <NoCredentialsError integration={integration} />;
  }

  if (userNeedsToLogin) {
    return <LoginToClay integration={integration} />;
  }

  if (didError) {
    return <LoginError integration={integration} />;
  }

  if (!didPostLoginCreds) {
    return <AllowAccess integration={integration} onAllow={finishOauthLogin} />;
  }

  return <FinishingAuth integration={integration} />;
}

export function ChatGPTOauthLogin() {
  const getOauthParams = (searchParams: URLSearchParams): ChatGPTOauthParams => {
    let responseType = searchParams.get('response_type');
    let clientId = searchParams.get('client_id');
    let redirectUrl = searchParams.get('redirect_uri');
    let scope = searchParams.get('scope');
    let state = searchParams.get('state');

    if (!(responseType && clientId && redirectUrl)) {
      const storedParams = getStoredOauthParams();

      if (!storedParams) {
        return undefined;
      }

      responseType = storedParams.responseType;
      clientId = storedParams.clientId;
      redirectUrl = storedParams.redirectUrl;
      scope = storedParams.scope;
      state = storedParams.state;
    }

    if (responseType && clientId && redirectUrl) {
      return { responseType, clientId, redirectUrl, scope, state };
    }

    return undefined;
  };

  const postOauthParams = async (oauthParams: ChatGPTOauthParams) =>
    apiRequest<any>(
      'GET',
      '/o/authorize/',
      {
        query: {
          response_type: oauthParams.responseType,
          client_id: oauthParams.clientId,
          redirect_uri: oauthParams.redirectUrl,
          scope: oauthParams.scope,
          state: oauthParams.state,
        },
      },
      {
        apiVersion: 'v2',
        noTimeZone: true,
      },
    );

  return (
    <OauthLogin
      getOauthParams={getOauthParams}
      postOauthParams={postOauthParams}
      integration="chatgpt"
    />
  );
}
